import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Injector, LOCALE_ID, InjectionToken } from '@angular/core';
import { SingletonCheckHelper } from '../helpers/singleton-check.helper';
import { TcmModalService } from '../modules/tcm-common/services/tcm-modal.service';
import { TenantCountry } from '../models/enums';
import { TcmTenantService } from './tcm-tenant.service';
import { LocalStorageService } from 'ngx-webstorage';
import { Subscription } from 'rxjs/internal/Subscription';
import { TcmCultureSystemService } from './tcm-culture-system-service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TcmApplicationManagerService {
  private isInitialized = false;
  private isRefreshModalDisplaying = false;
  private isCultureChangedModalDisplaying = false;

  private serverVersion: string;

  private get isClientVersionOutdated() {
    //return false;
    return this.serverVersion && this.serverVersion !== environment.VERSION;
  }

  private cultureChangingSubscription: Subscription;

  constructor(
    private localStorageService: LocalStorageService,
    private tcmCultureSystemService: TcmCultureSystemService,
    private readonly injector: Injector,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(LOCALE_ID) private readonly locale: string) {
    SingletonCheckHelper.ensureSingleton(this);
    this.cultureChangingSubscription = this.tcmCultureSystemService.cultureChanging$.subscribe(x => this.onCultureChangedAsync());
  }

  init() {
    this.isInitialized = true;
  }

  setupServerVersion(version: string) {
    this.serverVersion = version;

    this.onServerVersionChangedAsync();
  }

  setupTenantCountry(country: TenantCountry) {
    const tenantService = this.injector.get(TcmTenantService);
    tenantService.init(country);
  }

  refreshApplicationPageIfRequired(url?: string) {
    if (this.isClientVersionOutdated || this.tcmCultureSystemService.isCultureChanged) {
      this.refreshApplicationPage(url);
    }
  }

  private async onServerVersionChangedAsync() {
    if (this.isRefreshModalDisplaying || !this.isInitialized || !this.isClientVersionOutdated)
      return;

    try {
      this.isRefreshModalDisplaying = true;

      const modalService: TcmModalService = this.injector.get(TcmModalService);

      await modalService.showWarningModalAsync(
        x => x.Messages.MessageHeadersAndActions.Warning,
        x => x.Messages.WarningMessages.ApplicationUpdateRequired, "md", null, null, 'app-outdated');

      this.refreshApplicationPage();
    }
    finally {
      this.isRefreshModalDisplaying = false;
    }
  }

  private async onCultureChangedAsync() {
    if (this.isCultureChangedModalDisplaying || !this.isInitialized)
      return;

    try {
      this.isCultureChangedModalDisplaying = true;

      const modalService: TcmModalService = this.injector.get(TcmModalService);

      const makeRefresh = await modalService.showConfirmationModalAsync(
        x => x.Messages.MessageHeadersAndActions.Warning,
        x => x.Messages.WarningMessages.CultureChanged);

      if (makeRefresh === true) {
        this.refreshApplicationPage();
      }
    }
    finally {
      this.isCultureChangedModalDisplaying = false;
    }
  }

  private refreshApplicationPage(url?: string) {
    if (url) {
      this.document.location.href = url;
    }
    else {
      this.document.location.reload();
    }
  }
}
